import { useContext, useState } from 'preact/hooks';
import { ApiClientContext } from '../arcticapi';
import type { PlaceSearchResult } from '../arcticapi/types';
import {
  currentLocationToPlaceSearchResult,
  type PositionProvider,
} from './currentLocation';
import { useCurrentLocation } from './useCurrentLocation';
import {
  useLiveDeparturesMap,
  type LiveDeparturesMapState,
} from './useLiveDeparturesMap';
import { usePlaceSearch } from './usePlaceSearch';
import type { PlaceOption } from './places';

export interface LiveDeparturesSearchState {
  locationQuery: string;
  setLocationQuery: (value: string) => void;
  locationOptions: PlaceOption[];
  handleSubmit: (event?: SubmitEvent, selection?: PlaceOption) => boolean;
  handleUseCurrentLocation: () => Promise<boolean>;
  submitError: string | null;
  searchingLocations: boolean;
  currentLocationLoading: boolean;
  searchLocationsError: Error | null;
  mapState: LiveDeparturesMapState;
}

interface UseLiveDeparturesSearchOptions {
  initialDepartureStopId?: string;
  selectedStopAtcoCode?: string | null;
  currentLocationPositionProvider?: PositionProvider;
}

/**
 * Search/results controller for the live departures activity.
 *
 * Owns user input, place selection, and map state for finding a stop.
 * It deliberately does not fetch stop departures details; that boundary lives
 * in useStopDeparturesDetails so details pages can be reused elsewhere.
 */
export function useLiveDeparturesSearch(
  options: UseLiveDeparturesSearchOptions = {},
): LiveDeparturesSearchState {
  const client = useContext(ApiClientContext);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] =
    useState<PlaceSearchResult | null>(null);
  const currentLocation = useCurrentLocation({
    positionProvider: options.currentLocationPositionProvider,
  });
  const placeSearch = usePlaceSearch('', {
    currentCoordinates: currentLocation.currentLocation,
  });

  const selectedStopAtcoCode =
    options.selectedStopAtcoCode?.trim() ||
    options.initialDepartureStopId?.trim() ||
    null;
  const mapState = useLiveDeparturesMap({
    selectedLocation,
    preserveBounds: selectedStopAtcoCode !== null,
  });

  const handleSubmit = (
    event?: SubmitEvent,
    selection?: PlaceOption,
  ): boolean => {
    event?.preventDefault();
    setSubmitError(null);

    if (!client) {
      setSubmitError('Departures are unavailable in this preview context.');
      return false;
    }

    const place = selection?.place ?? placeSearch.resolveSelectedPlace();

    if (!place) {
      return false;
    }

    setSelectedLocation(place);

    return true;
  };

  const handleUseCurrentLocation = async (): Promise<boolean> => {
    setSubmitError(null);
    currentLocation.clearError();

    if (!client) {
      setSubmitError('Departures are unavailable in this preview context.');
      return false;
    }

    try {
      const location = await currentLocation.resolveCurrentLocation();
      const place = currentLocationToPlaceSearchResult(location);

      placeSearch.setResolvedPlace(place, location.label);
      setSelectedLocation(place);

      return true;
    } catch {
      return false;
    }
  };

  return {
    locationQuery: placeSearch.query,
    setLocationQuery: placeSearch.setQuery,
    locationOptions: placeSearch.options,
    handleSubmit,
    handleUseCurrentLocation,
    submitError,
    searchingLocations: placeSearch.loading || currentLocation.locating,
    currentLocationLoading: currentLocation.locating,
    searchLocationsError: placeSearch.error ?? currentLocation.error,
    mapState,
  };
}
